import React, { useRef, useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Box, Group, Image, Stack } from '@mantine/core'
import { AppSelect, AppTextarea } from '../../app/common/ui'
import { IoImageOutline, IoTrashOutline } from 'react-icons/io5'
import agent from '../../app/api/agent'
import type { CachePostDto } from '../../app/models/cache'
import { AppButton, AppInput, AppText } from '../../app/common/ui'

export const CACHE_POST_TYPE_OPTIONS = [
  { value: '3', label: 'Discussion' },
  { value: '1', label: 'Review' },
  { value: '2', label: 'Ownership Tip' },
]

const validationSchema = Yup.object({
  title: Yup.string().required('Title is required').max(500, 'Title is too long'),
  body: Yup.string().nullable(),
  postType: Yup.string().required(),
})

interface FormValues {
  title: string;
  body: string;
  postType: string;
}

interface Props {
  cacheId: string;
  postId?: string;
  initialValues?: {
    title?: string;
    body?: string | null;
    postType?: number;
    coverImageUrl?: string | null;
  };
  onSuccess: (post: CachePostDto) => void;
  onCancel?: () => void;
}

export default function CachePostForm ({ cacheId, postId, initialValues, onSuccess, onCancel }: Props) {
  const isEdit = !!postId
  const coverImageRef = useRef<HTMLInputElement>(null)
  const [coverFile, setCoverFile] = useState<File | null>(null)
  const [coverPreview, setCoverPreview] = useState<string | null>(initialValues?.coverImageUrl ?? null)
  const [coverUploading, setCoverUploading] = useState(false)

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setCoverFile(file)
    if (coverPreview && coverPreview.startsWith('blob:')) URL.revokeObjectURL(coverPreview)
    setCoverPreview(URL.createObjectURL(file))
  }

  const removeCover = () => {
    if (coverPreview?.startsWith('blob:')) URL.revokeObjectURL(coverPreview)
    setCoverFile(null)
    setCoverPreview(null)
    if (coverImageRef.current) coverImageRef.current.value = ''
  }

  const handleSubmit = async (values: FormValues, { setSubmitting }: { setSubmitting: (b: boolean) => void }) => {
    try {
      let post: CachePostDto
      if (isEdit) {
        post = await agent.Caches.editPost(cacheId, postId!, {
          title: values.title.trim(),
          body: values.body.trim() || null,
          postType: Number(values.postType),
          coverImageUrl: coverFile ? undefined : coverPreview,
        })
      } else {
        post = await agent.Caches.createPost(cacheId, {
          title: values.title.trim(),
          body: values.body.trim() || null,
          postType: Number(values.postType),
        })
      }

      if (coverFile) {
        setCoverUploading(true)
        try {
          const result = await agent.Caches.uploadPostCoverImage(cacheId, post.id, coverFile)
          post = { ...post, coverImageUrl: result.url }
        } finally {
          setCoverUploading(false)
        }
      }

      onSuccess(post)
    } catch {
      /* interceptor handles errors */
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={{
        title: initialValues?.title ?? '',
        body: initialValues?.body ?? '',
        postType: String(initialValues?.postType ?? 3),
      } as FormValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, setFieldValue, isSubmitting, errors, touched }) => (
        <Form>
          <Stack spacing="sm">
            <AppSelect
              label="Post type"
              data={CACHE_POST_TYPE_OPTIONS}
              value={values.postType}
              onChange={(v) => { if (v) setFieldValue('postType', v) }}
              size="sm"
              required
            />
            <AppInput
              label="Title"
              name="title"
              placeholder="What's this post about?"
              value={values.title}
              onChange={handleChange}
              maxLength={500}
              error={touched.title && errors.title ? errors.title : undefined}
              required
            />
            <AppTextarea
              label="Body"
              name="body"
              placeholder="Share your thoughts… (optional)"
              value={values.body}
              onChange={handleChange}
              minRows={4}
              autosize
              maxRows={14}
            />

            {/* Cover image picker */}
            <Box>
              <Group spacing={6} mb={coverPreview ? 8 : 0} align="center">
                <AppText size="xs" weight={600} color="dimmed" sx={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>Cover image</AppText>
                {!coverPreview && (
                  <AppButton
                    size="xs"
                    variant="subtle"
                    leftIcon={<IoImageOutline size={13} />}
                    onClick={() => coverImageRef.current?.click()}
                    type="button"
                    compact
                  >
                    Add
                  </AppButton>
                )}
              </Group>

              {coverPreview && (
                <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%', borderRadius: 6, overflow: 'hidden' }}>
                  <Image
                    src={coverPreview}
                    sx={{ maxHeight: 160, width: '100%', objectFit: 'cover' }}
                    alt="Cover preview"
                    radius="sm"
                  />
                  <Group spacing={6} mt={6}>
                    <AppButton
                      size="xs"
                      variant="subtle"
                      leftIcon={<IoImageOutline size={12} />}
                      onClick={() => coverImageRef.current?.click()}
                      type="button"
                    >
                      Change
                    </AppButton>
                    <AppButton
                      size="xs"
                      variant="subtle"
                      color="red"
                      leftIcon={<IoTrashOutline size={12} />}
                      onClick={removeCover}
                      type="button"
                    >
                      Remove
                    </AppButton>
                  </Group>
                </Box>
              )}

              <input
                ref={coverImageRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleCoverSelect}
              />
            </Box>

            <Group position="apart" mt="xs">
              {onCancel && (
                <AppButton type="button" variant="subtle" size="sm" onClick={onCancel}>
                  Cancel
                </AppButton>
              )}
              <AppButton
                type="submit"
                size="sm"
                color="ember"
                loading={isSubmitting || coverUploading}
                disabled={!values.title.trim()}
                ml={onCancel ? undefined : 'auto'}
              >
                {isEdit ? 'Save changes' : 'Post'}
              </AppButton>
            </Group>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}
